import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext';
import Footer from '../Footer';
import './ServicePage.css';

const ErevnaAnaptyxi = () => {
  const navigate = useNavigate();
  const { t, tArray, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const useCases = [
    {
      title: "Legal NLP for European Law",
      description: "NOMOKRATIA.AI — RAG architecture processing 3.5M+ legal documents across 15+ EU sources. Semantic search, multilingual embeddings, AI-powered legal reasoning."
    },
    {
      title: "Multilingual Speech AI",
      description: "Whisper Police — custom speech-to-text pipeline achieving 98.9% accuracy across 100+ languages. Deployed in production with Greek law enforcement."
    },
    {
      title: "Ancient Script Recognition",
      description: "PalaioScript AI — visual language models for handwritten manuscripts in Greek, Arabic, Syriac and Russian. Tested on 1M+ manuscripts at Mount Athos."
    },
    {
      title: "EU Regulatory AI Tools",
      description: "AI Act compliance classification wizard, NIS2 risk assessment engine, DSA transparency chatbot — developed for EU Commission tender."
    },
    {
      title: "Research Consortia",
      description: "Currently coordinating two EU research consortia: CyberSentinel (€5M, 8 partners) and IANUS (€19M, 10 partners) with DFKI, TU Chemnitz, University of Bologna, Transkribus/READ-COOP."
    }
  ];

  const faqs = [
    {
      question: "What is AI Research & Development?",
      answer: "We develop specialized AI systems adapted to the needs of the Greek public and private sector — from NLP models for Greek to computer vision and regulatory compliance tools. We build custom solutions for problems off-the-shelf tools can't solve."
    },
    {
      question: "How do you develop NLP models for Greek?",
      answer: "We use large Greek text datasets and advanced Machine Learning techniques. Our models are trained specifically for the complex grammar and morphology of Greek — both modern and ancient variants."
    },
    {
      question: "What AI technologies do you use?",
      answer: "We work with the most advanced platforms: OpenAI, Claude AI, Gemini, Mistral (European LLM), and develop custom models with TensorFlow, PyTorch and Hugging Face when required. All on European infrastructure."
    },
    {
      question: "Do you offer training for teams?",
      answer: "Yes, we provide training and knowledge transfer programs. We train your teams on using and maintaining the AI systems we develop — ensuring long-term autonomy."
    },
    {
      question: "Do you collaborate with universities?",
      answer: "Yes, we collaborate with academic institutions and research centers. We are currently coordinating two Horizon Europe consortia (CyberSentinel, IANUS) with partners including DFKI (Germany), TU Chemnitz, University of Bologna, and Transkribus/READ-COOP (Austria)."
    }
  ];

  const serviceKey = 'servicePage.erevnaAnaptyxi';
  const hasTranslation = language !== 'el';

  const title = hasTranslation ? t(`${serviceKey}.title`) : 'AI Research & Development';
  const subtitle = hasTranslation ? t(`${serviceKey}.subtitle`) : 'Custom solutions for problems off-the-shelf tools can\'t solve.';
  const description = hasTranslation ? t(`${serviceKey}.description`) : 'We develop specialized AI systems for complex challenges that require custom research, novel architectures, and domain-specific expertise — from legal NLP to ancient manuscript recognition.';

  const translatedUseCases = hasTranslation ? tArray(`${serviceKey}.useCases`) : [];
  const translatedFaqs = hasTranslation ? tArray(`${serviceKey}.faqs`) : [];

  const displayUseCases = translatedUseCases.length > 0 ? translatedUseCases : useCases;
  const displayFaqs = translatedFaqs.length > 0 ? translatedFaqs : faqs;

  const ctaTitle = hasTranslation ? t(`${serviceKey}.ctaTitle`) : 'Develop Custom AI';
  const ctaText = hasTranslation ? t(`${serviceKey}.ctaText`) : 'Talk to our specialists to design your next AI solution together.';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={hasTranslation ? description : "Εξειδικευμένα συστήματα AI για τον ελληνικό δημόσιο και ιδιωτικό τομέα. NLP για ελληνικά, Computer Vision, Predictive Analytics. ISO 42001."} />
        <link rel="canonical" href="https://ai-agency.gr/services/erevna-anaptyxi" />
        
        {/* Schema.org Service Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Έρευνα & Ανάπτυξη AI",
            "provider": {
              "@type": "Organization",
              "name": "PROTOS AI AGENCY",
              "url": "https://ai-agency.gr/"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Greece"
            },
            "description": "Εξειδικευμένα συστήματα AI προσαρμοσμένα στον ελληνικό τομέα. Custom NLP μοντέλα, Computer Vision, Machine Learning με ISO 42001 πιστοποίηση."
          })}
        </script>

        {/* Schema.org FAQPage Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": displayFaqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": hasTranslation ? t('servicePage.breadcrumb.home') : "Αρχική",
                "item": "https://ai-agency.gr/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": hasTranslation ? t('servicePage.breadcrumb.services') : "Υπηρεσίες",
                "item": "https://ai-agency.gr/#services"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Έρευνα & Ανάπτυξη",
                "item": "https://ai-agency.gr/services/erevna-anaptyxi"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="service-page">
        <header className="service-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button 
              onClick={() => navigate('/')}
              className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-2 mb-4"
            >
              <span>←</span>
              <span>{t('nav.backToHome')}</span>
            </button>
            
            {/* Breadcrumb Navigation */}
            <nav aria-label="breadcrumb" className="text-sm text-gray-400 mb-2">
              <ol className="flex items-center space-x-2">
                <li>
                  <a href="/" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.home')}</a>
                </li>
                <li><span>/</span></li>
                <li>
                  <a href="/#services" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.services')}</a>
                </li>
                <li><span>/</span></li>
                <li className="text-gray-300" aria-current="page">{hasTranslation ? title : "Έρευνα & Ανάπτυξη"}</li>
              </ol>
            </nav>
          </div>
        </header>

        <section className="service-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>
        </section>

        <section className="service-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="prose prose-lg prose-invert max-w-none">
              {hasTranslation ? (
                <p className="text-lg text-gray-300 leading-relaxed">
                  {description}
                </p>
              ) : (
                <>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Η Τεχνητή Νοημοσύνη δεν είναι μια λύση που ταιριάζει σε όλους. Κάθε οργανισμός έχει μοναδικές ανάγκες, προκλήσεις και στόχους που απαιτούν εξειδικευμένες λύσεις. Η ομάδα Έρευνας και Ανάπτυξης μας αφιερώνεται στη δημιουργία προσαρμοσμένων συστημάτων AI που ανταποκρίνονται ακριβώς στις απαιτήσεις των ελληνικών δημόσιων και ιδιωτικών οργανισμών.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Με εμπειρία σε Machine Learning, Deep Learning, Natural Language Processing και Computer Vision, αναπτύσσουμε λύσεις που βασίζονται στις πιο προηγμένες τεχνολογίες, προσαρμοσμένες στο ελληνικό περιβάλλον. Συνεργαζόμαστε με τις κορυφαίες πλατφόρμες AI, συμπεριλαμβανομένων των OpenAI, Claude AI και Gemini, αλλά και αναπτύσσουμε δικά μας μοντέλα όταν αυτό είναι απαραίτητο.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Η προσέγγισή μας ξεκινά πάντα με βαθιά κατανόηση του προβλήματος που πρέπει να λυθεί. Συνεργαζόμαστε στενά με τους πελάτες μας για να κατανοήσουμε τις διαδικασίες τους, τους περιορισμούς τους και τους στόχους τους. Στη συνέχεια, σχεδιάζουμε και αναπτύσσουμε λύσεις που όχι μόνο λειτουργούν τεχνικά, αλλά και ενσωματώνονται ομαλά στις υπάρχουσες υποδομές και ροές εργασίας.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Ειδικευόμαστε στην ανάπτυξη μοντέλων για την ελληνική γλώσσα, που παρουσιάζει μοναδικές προκλήσεις λόγω της πολυπλοκότητας της γραμματικής και της πλούσιας μορφολογίας της. Τα μοντέλα μας έχουν εκπαιδευτεί σε μεγάλα σύνολα ελληνικών δεδομένων και επιτυγχάνουν υψηλή ακρίβεια σε εργασίες όπως η κατανόηση φυσικής γλώσσας, η μετάφραση και η ανάλυση συναισθήματος.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Για οργανισμούς που επιθυμούν να αναπτύξουν εσωτερικές ικανότητες AI, προσφέρουμε προγράμματα εκπαίδευσης και μεταφοράς γνώσης. Εκπαιδεύουμε τις ομάδες σας στη χρήση και συντήρηση των συστημάτων που αναπτύσσουμε, διασφαλίζοντας μακροπρόθεσμη αυτονομία και επιτυχία.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Συνεργαζόμαστε επίσης με ακαδημαϊκά ιδρύματα και ερευνητικά κέντρα για την προώθηση της έρευνας στην Τεχνητή Νοημοσύνη στην Ελλάδα. Συμμετέχουμε σε ερευνητικά προγράμματα, δημοσιεύουμε επιστημονικές εργασίες και συμβάλλουμε στην ανάπτυξη του οικοσυστήματος AI στη χώρα.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Όλα τα συστήματα που αναπτύσσουμε σχεδιάζονται με γνώμονα την ασφάλεια, την αξιοπιστία και την ηθική χρήση της AI. Ακολουθούμε τα διεθνή πρότυπα ISO 42001 για τη διαχείριση συστημάτων AI και διασφαλίζουμε ότι οι λύσεις μας είναι διαφανείς, εξηγήσιμες και ελέγξιμες.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="use-cases-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {t('servicePage.useCasesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayUseCases.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <h3 className="text-xl font-bold mb-3 text-cyan-400">{useCase.title}</h3>
                  <p className="text-gray-300">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-black/30 relative z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {t('servicePage.faqTitle')}
            </h2>
            <div className="space-y-6">
              {displayFaqs.map((faq, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3 text-cyan-400">{faq.question}</h3>
                  <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="py-16 relative z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-8 text-white">
              {t('servicePage.relatedServicesTitle')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <a href="/services/asfaleia" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-red-400/40 transition-all">
                <h3 className="text-xl font-bold mb-2 text-red-400">{t('services.asfaleia')}</h3>
                <p className="text-gray-400">{t('servicePage.relatedDescriptions.asfaleia')}</p>
              </a>
              <a href="/services/politistiki-klironomia" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-purple-400/40 transition-all">
                <h3 className="text-xl font-bold mb-2 text-purple-400">{t('services.politistikiKlironomia')}</h3>
                <p className="text-gray-400">{t('servicePage.relatedDescriptions.politistikiKlironomia')}</p>
              </a>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              {ctaTitle}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              {ctaText}
            </p>
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all transform hover:scale-105"
            >
              {t('servicePage.ctaButton')}
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ErevnaAnaptyxi;